import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import {
  ListItem,
  ListItemText,
  Typography,
  List,
  Divider,
  Stack,
  Box,
  SvgIcon,
  ListItemIcon,
  Skeleton,
  LinearProgress,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import { CurrencyDollar } from '@styled-icons/heroicons-outline'
import { capitalize, upperCase } from 'lodash'
import PropTypes from 'prop-types'
import React, { Children, useEffect, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import api from 'api'
import { CACHE_TAG_CONFIGURED_SERVICES_LIST, CACHE_TAG_CONFIGURED_SERVICE_DETAILS } from 'api/cacheTagTypes'
import { useLazyGetServiceDetailsQuery } from 'api/configured-service/getServiceDetails'
import { useLazyGetConfiguredServiceContainersQuery } from 'api/inventory/configuredServiceContainer'
import { ReactComponent as CalendarDay } from 'assets/CalendarDay.svg'
import { ReactComponent as CalendarMoney } from 'assets/CalendarMoney.svg'
import { ReactComponent as CalendarWeek } from 'assets/CalendarWeek.svg'
import { ReactComponent as CalendarXMark } from 'assets/CalendarXMark.svg'
import { ReactComponent as CalendarXMoney } from 'assets/CalendarXMoney.svg'
import { ReactComponent as MoneyCheckDollarPenIcon } from 'assets/MoneyCheckDollarPen.svg'
import MethodChip from 'components/common/chip/MethodChip'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import { transformInventoryContainers } from 'components/configured-service/accordions/ContainersAccordion/utils'
import LocationMapImage from 'components/customer-details/content/location/LocationMapImage'
import ServiceActionTypeChip from 'components/services/ServiceActionTypeChip'
import ServiceMaterialTypeChip from 'components/services/ServiceMaterialTypeChip'
import InteractiveTagsList from 'components/tags/InteractiveTagsList'
import { CONFIGURED_SERVICE_TABS_ID } from 'containers/configured-service/settings'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import { selectIsInventoryFeatureEnabled } from 'data/tenant/tenantSettingsSelectors'
import { getConfiguredServiceDetailsPageUrl } from 'router/routes'
import T from 'T'
import { createDateFromString, formatDateToFEFormatDateFns } from 'utils/date'
import { handleError } from 'utils/error'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { combineAddressInfo } from 'utils/helper'
import { get } from 'utils/lodash'
import { generateRandomId } from 'utils/string'
import { getPricingPeriod } from 'utils/service'

const listItemSx = { flex: 'inherit', width: 125 }

const ServiceContent = ({ isOpen, serviceId, showServiceName = false, isTagsViewOnly = false, ...rest }) => {
  const isInventoryFeatureEnabled = useSelector(selectIsInventoryFeatureEnabled, shallowEqual)
  const dispatch = useDispatch()
  const [getServiceDetails, { isFetching: isLoading, data: serviceDetailsData }] = useLazyGetServiceDetailsQuery()
  const [getInventoryContainers, { isFetching: isGetInventoryContainersLoading, data: inventoryContainersData }] =
    useLazyGetConfiguredServiceContainersQuery()
  const customerDetails = useSelector(state => get(state, 'CustomersReducer.customerDetails', null))
  const inventoryContainers = useMemo(
    () => transformInventoryContainers(get(inventoryContainersData, 'containers', [])),
    [inventoryContainersData]
  )

  const {
    accountId,
    price,
    locationId,
    latitude,
    longitude,
    startDate,
    endDate,
    billingStartDate,
    billingEndDate,
    purchaseOrderNumber,
    tagDetails,
  } = useMemo(
    () => ({
      accountId: get(serviceDetailsData, 'accountId'),
      price: get(serviceDetailsData, 'price', 0),
      locationId: get(serviceDetailsData, 'location.locationId'),
      latitude: get(serviceDetailsData, 'location.latitude'),
      longitude: get(serviceDetailsData, 'location.longitude'),
      startDate: formatDateToFEFormatDateFns(createDateFromString(get(serviceDetailsData, 'schedule.startDate'))) || '--',
      endDate: formatDateToFEFormatDateFns(createDateFromString(get(serviceDetailsData, 'schedule.endDate'))) || '--',
      billingStartDate: formatDateToFEFormatDateFns(createDateFromString(get(serviceDetailsData, 'billingSchedule.startDate'))) || '--',
      billingEndDate: formatDateToFEFormatDateFns(createDateFromString(get(serviceDetailsData, 'billingSchedule.endDate'))) || '--',
      purchaseOrderNumber: get(serviceDetailsData, 'advanced.purchaseOrderNumber', '--'),
      tagDetails: get(serviceDetailsData, 'tagDetails', {}),
    }),
    [serviceDetailsData]
  )

  const { serviceName, pricingPeriod, pricingZone, actionType, methodType, measureUnit, measureVolume, materialType, materialColor } =
    useMemo(
      () => ({
        serviceName: get(serviceDetailsData, 'priceServiceDetail.serviceName', ''),
        pricingPeriod: getPricingPeriod(get(serviceDetailsData, 'periodPer'), get(serviceDetailsData, 'periodInterval')) ?? '--',
        pricingZone: get(serviceDetailsData, 'pricingZone.zoneName') ?? '--',
        actionType: get(serviceDetailsData, 'action.actionType') ?? '',
        methodType: get(serviceDetailsData, 'method.methodType') ?? '',
        measureUnit: get(serviceDetailsData, 'measure.unit') ?? '',
        measureVolume: get(serviceDetailsData, 'measure.volume') ?? 0,
        materialType: get(serviceDetailsData, 'material.materialType'),
        materialColor: get(serviceDetailsData, 'material.color'),
      }),
      [serviceDetailsData]
    )

  const locationDetails = useMemo(
    () => get(customerDetails, 'locations', []).find(location => location.id === locationId),
    [customerDetails, locationId]
  )
  const locationName = useMemo(() => get(locationDetails, 'locationName', ''), [locationDetails])
  const locationAddress = useMemo(() => get(locationDetails, 'address', {}), [locationDetails])
  const lineAndUnitNumber = combineAddressInfo({
    line1: get(locationAddress, 'line1'),
    unitNumber: get(locationAddress, 'unitNumber'),
  })
  const cityStateZip = combineAddressInfo({
    city: get(locationAddress, 'city'),
    state: get(locationAddress, 'state'),
    zipCode: get(locationAddress, 'zipCode'),
  })

  const measureLabel = `${measureVolume} ${getMeasureUnit(measureUnit)}`
  const isContainersLoading = isLoading || isGetInventoryContainersLoading

  const onSaveTags = (data, closeCallback) => {
    closeCallback()
    dispatch(api.util.invalidateTags([CACHE_TAG_CONFIGURED_SERVICES_LIST, { type: CACHE_TAG_CONFIGURED_SERVICE_DETAILS, id: serviceId }]))
  }

  // Replace is service-scoped, so route to the service detail page and let it open the
  // Replace dialog for this container (via ?replaceContainerId), rather than mounting the
  // service-coupled dialog here on the customer page.
  const navigate = useNavigate()
  const handleReplace = event => {
    const { containerId } = event.currentTarget.dataset
    if (!containerId) return
    const params = new URLSearchParams()
    if (accountId) params.set('accountId', accountId)
    if (locationId) params.set('locationId', locationId)
    params.set('tab', CONFIGURED_SERVICE_TABS_ID.DETAILS_PRICING_TAB_ID)
    params.set('replaceContainerId', containerId)
    navigate(`${getConfiguredServiceDetailsPageUrl(serviceId)}?${params.toString()}`)
  }

  useEffect(() => {
    if (isOpen) {
      getServiceDetails(serviceId).unwrap().catch(handleError)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      getInventoryContainers({ configuredServiceId: serviceId }).unwrap().catch(handleError)
    }
  }, [isOpen])

  return (
    <Grid container columnGap={2} pr={2} py={2} {...rest}>
      <Grid
        size={{
          xs: 12,
          md: 'grow',
        }}
      >
        <ListItem sx={{ pt: 0 }}>
          <ListItemText
            primary={
              <>
                {showServiceName && (
                  <Box alignItems="center" display="flex" gap={1}>
                    <Typography noWrap variant="h5">
                      {serviceName}
                    </Typography>
                    <Divider flexItem orientation="vertical" />
                    {!isLoading && (
                      <Typography fontWeight={700} variant="h5">
                        <HHDisplayMoney formatToDollars={false} value={price} />
                      </Typography>
                    )}
                  </Box>
                )}
                <Typography noWrap fontWeight={500} variant="h6">
                  {isLoading ? <Skeleton width={200} /> : lineAndUnitNumber}
                </Typography>
                <Typography noWrap fontWeight={500} variant="h6">
                  {isLoading ? <Skeleton width={100} /> : cityStateZip}
                </Typography>
              </>
            }
            secondary={isLoading ? <Skeleton width={150} /> : locationName}
            slotProps={{
              secondary: { variant: 'h6', fontWeight: 400, noWrap: true },
            }}
          />
        </ListItem>
        <ListItem>
          <InteractiveTagsList
            disableAccount
            disableLocation
            accountId={accountId}
            data={{
              tagDetails,
              name: serviceName,
              configuredServiceId: serviceId,
            }}
            isViewOnly={isTagsViewOnly}
            onSave={onSaveTags}
          />
        </ListItem>
        <ListItem divider>
          <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <SvgIcon>
              <CalendarDay />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText
            primary={isLoading ? <Skeleton width={50} /> : startDate}
            secondary={capitalize(T.START_DATE)}
            slotProps={{
              primary: { variant: 'subtitle1', fontWeight: 500 },
              secondary: { variant: 'caption' },
            }}
            sx={listItemSx}
          />
          <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <CalendarXMark />
          </ListItemIcon>
          <ListItemText
            primary={endDate}
            secondary={capitalize(T.END_DATE)}
            slotProps={{
              primary: { variant: 'subtitle1', fontWeight: 500 },
              secondary: { variant: 'caption' },
            }}
            sx={listItemSx}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <SvgIcon>
              <CalendarMoney />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText
            primary={isLoading ? <Skeleton width={50} /> : billingStartDate}
            secondary={capitalize(T.BILLING_START_DATE)}
            slotProps={{
              primary: { variant: 'subtitle1', fontWeight: 500 },
              secondary: { variant: 'caption' },
            }}
            sx={listItemSx}
          />
          <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <SvgIcon>
              <CalendarXMoney />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText
            primary={isLoading ? <Skeleton width={50} /> : billingEndDate}
            secondary={capitalize(T.BILLING_END_DATE)}
            slotProps={{
              primary: { variant: 'subtitle1', fontWeight: 500 },
              secondary: { variant: 'caption' },
            }}
            sx={listItemSx}
          />
        </ListItem>
        <ListItem divider>
          <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <SvgIcon>
              <CalendarWeek />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText
            primary={isLoading ? <Skeleton width={50} /> : pricingPeriod}
            secondary={capitalize(T.PRICE_PERIOD)}
            slotProps={{
              primary: { variant: 'subtitle1', fontWeight: 500, noWrap: true },
              secondary: { variant: 'caption' },
            }}
            sx={listItemSx}
          />
          <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <SvgIcon>
              <CurrencyDollar />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText
            primary={isLoading ? <Skeleton width={50} /> : pricingZone}
            secondary={capitalize(T.PRICING_ZONE)}
            slotProps={{
              primary: { variant: 'subtitle1', fontWeight: 500, noWrap: true },
              secondary: { variant: 'caption' },
            }}
            sx={listItemSx}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
            <MoneyCheckDollarPenIcon height={24} width={24} />
          </ListItemIcon>
          <ListItemText
            primary={isLoading ? <Skeleton width={50} /> : purchaseOrderNumber}
            secondary={T.PURCHASE_ORDER_LABEL}
            slotProps={{
              primary: { variant: 'subtitle1', fontWeight: 500 },
              secondary: { variant: 'caption' },
            }}
          />
        </ListItem>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 'grow',
        }}
      >
        <Stack spacing={1}>
          <Box>
            <LocationMapImage
              dynamicWidth
              showPlaceholder
              containerId={`cs-details-map-container-${generateRandomId(10)}`}
              height={200}
              latitude={parseFloat(latitude)}
              longitude={parseFloat(longitude)}
              showInvalidAlert={false}
              width="100%"
            />
          </Box>
          {(actionType || methodType) && (
            <Box alignItems="center" display="flex" gap={1}>
              {actionType && <ServiceActionTypeChip action={actionType} size="small" />}
              {methodType && <MethodChip methodType={methodType} />}
            </Box>
          )}

          {(actionType || methodType) && <Divider />}

          {(measureUnit || materialType) && (
            <Box alignItems="center" display="flex" gap={1}>
              {measureUnit && <MeasureChip label={measureLabel} measureUnit={measureUnit} />}
              {materialType && <ServiceMaterialTypeChip label={materialType} materialColor={materialColor} />}
            </Box>
          )}

          {isContainersLoading && <LinearProgress />}
          {inventoryContainers.length > 0 && (
            <List sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <ListItem>
                <ListItemText
                  primary={upperCase(T.CONTAINERS)}
                  slotProps={{
                    primary: { variant: 'h6', fontWeight: 700 },
                  }}
                />
                <ListItemText
                  primary="ID"
                  slotProps={{
                    primary: { variant: 'h6', fontWeight: 700, textAlign: 'right' },
                  }}
                />
              </ListItem>
              {Children.toArray(
                inventoryContainers.map(container => (
                  <ListItem
                    secondaryAction={
                      isInventoryFeatureEnabled && get(container, 'id') ? (
                        <IconButton
                          aria-label={T.REPLACE}
                          data-container-id={get(container, 'id')}
                          edge="end"
                          size="small"
                          title={T.REPLACE}
                          onClick={handleReplace}
                        >
                          <SwapHorizOutlinedIcon fontSize="small" />
                        </IconButton>
                      ) : null
                    }
                  >
                    <ListItemText
                      primary={get(container, 'containerName', '')}
                      slotProps={{
                        primary: { variant: 'h6', fontWeight: 500, noWrap: true },
                      }}
                    />
                    <ListItemText
                      primary={get(container, 'containerDescription', '') || get(container, 'serialNumber', '')}
                      slotProps={{
                        primary: { variant: 'h5', fontWeight: 500, noWrap: true, textAlign: 'right' },
                      }}
                    />
                  </ListItem>
                ))
              )}
            </List>
          )}
        </Stack>
      </Grid>
    </Grid>
  )
}

ServiceContent.propTypes = {
  isOpen: PropTypes.bool,
  serviceId: PropTypes.string.isRequired,
  showServiceName: PropTypes.bool,
  isTagsViewOnly: PropTypes.bool,
}

export default ServiceContent
